import { QuoteRatingGetResponse, QuoteRatingSearchResponse } from '@lio/api-types';
import { quoteRatingStatus } from '@models/quoteRating';
import { formVersionQueryKeys } from '@services/formVersion/keys';
import { quoteFormQueryKeys } from '@services/quoteForm/keys';
import { quoteItemQueryKey } from '@services/quoteItem';
import { queryKeys as underwritingReferralQueryKeys } from '@services/underwritingReferral';
// Importing directly instead of via barrel to avoid circular re-export between index.ts and this file.
import { searchLastSuccessfulRatingParams } from './quoteRating.helpers';
import { quoteRatingQueryKey } from '@services/quoteRating/keys';
import { subjectivityQueryKeys } from '@services/subjectivity';
import { useQueryClient, useQuery, InfiniteData } from '@tanstack/react-query';
import { useClient } from '@utils/api-client';
import { differenceInMilliseconds } from 'date-fns';

export const useGetQuoteRating = (quoteId?: string, quoteRatingId?: string) => {
  const client = useClient();
  const queryClient = useQueryClient();
  const url = `quote/${quoteId}/rating/${quoteRatingId}`;
  const queryFunc = useQuery({
    queryKey: quoteRatingQueryKey.getWithParams(quoteId, quoteRatingId),
    queryFn: () => client(url).json<QuoteRatingGetResponse>(),
    enabled: !!quoteId && !!quoteRatingId,
    refetchInterval: (query) => {
      const { data } = query.state;
      if (data?.status === 'Pending' || data?.status === 'InProgress') {
        const queryStartedAt = new Date(data.createdAt);
        const now = new Date();
        const msSinceStarted = differenceInMilliseconds(now, queryStartedAt);
        if (msSinceStarted < 3000) {
          return 1000;
        }
        if (msSinceStarted < 10000) {
          return 2000;
        }
        return 5000;
      }
      return false;
    },
  });
  if (queryFunc.isSuccess) {
    const { data } = queryFunc;
    if (data.status === quoteRatingStatus.Succeeded) {
      queryClient.setQueryData<InfiniteData<QuoteRatingSearchResponse>>(
        quoteRatingQueryKey.searchWithParams(data.quoteId, searchLastSuccessfulRatingParams),
        (previousData) => {
          if (previousData) {
            const newData = {
              ...previousData,
              pages: previousData.pages.map((page) => ({
                items: [data],
                pagination: page.pagination,
              })),
            };
            return newData;
          }
          return previousData;
        },
      );
      queryClient.invalidateQueries({
        queryKey: [underwritingReferralQueryKeys.underwritingActivity],
      });
      queryClient.invalidateQueries({ queryKey: ['quote', { quoteId: data.quoteId }] });
      queryClient.invalidateQueries({ queryKey: ['underwritingTasks'] });
      queryClient.invalidateQueries({ queryKey: subjectivityQueryKeys.search(quoteId) });
      queryClient.invalidateQueries({
        queryKey: quoteItemQueryKey.searchQuoteItemCoverages({ quoteId: data.quoteId }),
      });
      queryClient.invalidateQueries({
        queryKey: quoteItemQueryKey.searchWithParams({ quoteId: data.quoteId }),
      });
      queryClient.invalidateQueries({
        queryKey: quoteFormQueryKeys.get(data.quoteId),
      });
      queryClient.invalidateQueries({
        queryKey: formVersionQueryKeys.get(data.quoteId),
      });
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [resource, type, params] = query.queryKey as [
            string,
            string,
            { quoteIdSource?: string; quoteIdTarget?: string },
          ];
          return resource === 'quote' && type === 'diff-forms' && params?.quoteIdTarget === data.quoteId;
        },
      });
    }
  }
  return queryFunc;
};
